import { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';

import { BuildingInspectionListTable } from './Components/BuildingInspectionListTable';
import { PropertySelector } from './Components/PropertySelector';
import { GenerateBuildingInspectionModal } from './Components/GenerateBuildingInspectionModal';
import { DeleteBuildingInspectionConfirmationModal } from './Components/DeleteBuildingInspectionConfirmationModal';

import {
  defaultBuildingInspection,
  getBuildingInspectionInitialValues,
  BuildingInspectionValidationSchema,
} from './Components/FormikHelpers';

import { getPropertyList } from '../../../Inventory/pages/FacilityInventory/Helpers/Api';
import { validateStep } from './Components/FormikHelpers/ValidationSchema';

import {
  createBuildingInspection,
  getBuildingInspectionList,
  updateBuildingInspection,
  deleteBuildingInspection,
} from './Components/FormikHelpers/Api';
import { setLoading } from '../../../../store/actions';

const BuildingInspection = () => {
  const dispatch = useDispatch();

  const [properties, setProperties] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);

  const [selectedFilters, setSelectedFilters] = useState({
    property_id: '',
    building_name: '',
    month: new Date().toLocaleString('default', { month: 'long' }),
    status: 'all',
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    setUser(currentUser);

    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch {
      Swal.fire('Error', 'Failed to fetch properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getUserFromToken]);

  const fetchInspections = useCallback(
    async (propertyId, building_name, month, status = 'all') => {
      try {
        dispatch(setLoading(true));
        const params = {
          propertyId: propertyId || undefined,
          building_name: building_name || undefined,
          month: month || undefined,
          status: status || 'all',
        };

        const response = await getBuildingInspectionList(params);
        setInspections(response?.data || response || []);
      } catch (error) {
        console.error('Failed to fetch building inspections:', error);
        setInspections([]);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  const submitInspection = async (values) => {
    const isValid = await validateStep(
      values
    );
    if (!isValid) {
      throw new Error('Validation failed');
    }

    const normalizePictures = (items = []) =>
      items.map((item) => ({
        ...item,
        pictures: (item.pictures || []).map((photo) => {
          if (typeof photo === 'string') return photo;
          return photo?.url || photo?.fileUrl || photo;
        }),
      }));

    const payload = {
      ...values,
      interior: normalizePictures(values.interior),
      elevators: normalizePictures(values.elevators),
      underground: normalizePictures(values.underground),
      outside: normalizePictures(values.outside),
      misc_others: normalizePictures(values.misc_others),
    };

    if (selectedInspection?._id) {
      await updateBuildingInspection(selectedInspection._id, payload);
      return selectedInspection._id;
    }

    const response = await createBuildingInspection(payload);
    return response._id;
  };

  const formik = useFormik({
    initialValues: defaultBuildingInspection,
    validationSchema: BuildingInspectionValidationSchema,
    enableReinitialize: true,
  });

  const handleAddClick = () => {
    setSelectedInspection(null);
    formik.resetForm({
      values: {
        ...defaultBuildingInspection,
        property_details: {
          property_id: selectedFilters.property_id,
          building_name: selectedFilters.building_name,
        },
      },
    });
    setIsModalOpen(true);
  };

  const handleEditClick = async (inspection) => {
    setSelectedInspection(inspection);

    try {
      setIsAutoFilling(true);
      const values = await getBuildingInspectionInitialValues(inspection._id);

      formik.setValues({
        ...values,
        _id: inspection._id,
        property_details: values.property_details || {
          property_id: selectedFilters.property_id,
          building_name: selectedFilters.building_name,
        },
      });
    } catch (err) {
      Swal.fire('Error', 'Failed to load inspection details for editing', 'error');
    } finally {
      setIsAutoFilling(false);
      setIsModalOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedInspection?._id) return;

    try {
      dispatch(setLoading(true));
      await deleteBuildingInspection(selectedInspection._id);
      Swal.fire('Deleted', 'Building Inspection deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setSelectedInspection(null);
      fetchInspections(selectedFilters.property_id, selectedFilters.building_name, selectedFilters.month, selectedFilters.status);
    } catch (error) {
      Swal.fire('Error', 'Failed to delete inspection', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePropertyChange = (newFilters) => {
    setSelectedFilters(newFilters);
  };

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const { property_id, building_name, month } = selectedFilters;

  const loadInspections = useCallback(() => {
    fetchInspections(
      property_id,
      building_name,
      month,
      selectedFilters.status
    );
  }, [fetchInspections, property_id, building_name, month, selectedFilters.status]);

  useEffect(() => {
    loadInspections();
  }, [loadInspections]);

  const refreshInspections = loadInspections;

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Building Inspections</title>
      </MetaTags>

      <div className="container-fluid">
        <Breadcrumbs
          title="Maintenance"
          breadcrumbItem="Building Inspections"
        />

        <PropertySelector
          propertyList={properties}
          selectedFilters={selectedFilters}
          onPropertySelectionChange={handlePropertyChange}
          onAddClick={handleAddClick}
          isLoading={isAutoFilling}
          user={user}
        />

        <BuildingInspectionListTable
          items={inspections}
          onDelete={(inspection) => {
            setSelectedInspection(inspection);
            setIsDeleteModalOpen(true);
          }}
          onEdit={handleEditClick}
          userRole={user?.role || 'other'}
          onRefresh={() =>
            fetchInspections(selectedFilters.property_id, selectedFilters.building_name, selectedFilters.month, selectedFilters.status)
          }
        />

        <GenerateBuildingInspectionModal
          formik={formik}
          filters={selectedFilters}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          isEdit={!!selectedInspection}
          onRefresh={refreshInspections}
          submitInspection={submitInspection}
          userRole={user?.role || 'other'}
          userId={user?.id || ''}
        />

        <DeleteBuildingInspectionConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={handleDeleteConfirm}
          inspection={selectedInspection}
        />
      </div>
    </div>
  );
};

export { BuildingInspection };